import * as React from "react"
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type { ChartConfig } from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { DashboardChartsData } from "@/types"
import { formatMonthYear } from "@/utils/date"

const truncateLabel = (label: string, maxLength = 12) => {
  if (label.length <= maxLength) return label
  return `${label.slice(0, maxLength)}...`
}

interface DashboardChartsProps {
  data: DashboardChartsData
}

export function DashboardCharts({ data }: DashboardChartsProps) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <RequestsByDayChart requestsByDayData={data.requestsByDay} />
      <QuotesByMonthChart data={data.quotesByMonth} />
      <TopSuppliersChart data={data.topSuppliers} />
      {data.activeUsers && <ActiveUsersChart data={data.activeUsers} />}
      <SectorActivityChart data={data.sectorActivity} />
      <TopProductsChart data={data.topProducts} />
    </div>
  )
}

function RequestsByDayChart({ requestsByDayData }: { requestsByDayData: DashboardChartsData["requestsByDay"] }) {
  const [timeRange, setTimeRange] = React.useState<"7d" | "30d" | "90d">("30d")

  const chartConfig = {
    total: {
      label: "Total Creadas",
      color: "var(--chart-1)",
    },
    approved: {
      label: "Aprobadas",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig

  // Simply use the pre-filtered data from the backend
  const data = requestsByDayData[timeRange]

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex flex-col gap-1">
          <CardTitle>Solicitudes por Día</CardTitle>
          <CardDescription>Total creadas vs aprobadas</CardDescription>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[130px]" aria-label="Seleccionar período">
            <SelectValue placeholder="Últimos 30 días" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Últimos 7 días</SelectItem>
            <SelectItem value="30d">Últimos 30 días</SelectItem>
            <SelectItem value="90d">Últimos 3 meses</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full aspect-auto min-w-0">
          <AreaChart
            accessibilityLayer
            data={data}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <defs>
              <linearGradient id="fillTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-total)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-total)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="fillApproved" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-approved)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-approved)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value: string) => {
                const date = new Date(value)
                return date.toLocaleDateString("es-AR", {
                  month: "short",
                  day: "numeric",
                })
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value: string) => {
                    return new Date(value).toLocaleDateString("es-AR", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  }}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="approved"
              type="natural"
              fill="url(#fillApproved)"
              stroke="var(--color-approved)"
              stackId="a"
            />
            <Area
              dataKey="total"
              type="natural"
              fill="url(#fillTotal)"
              stroke="var(--color-total)"
              stackId="a"
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function QuotesByMonthChart({ data }: { data: DashboardChartsData["quotesByMonth"] }) {
  const chartConfig = {
    count: {
      label: "Cotizaciones",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig

  // Transform data to format months in Spanish
  const formattedData = React.useMemo(
    () =>
      data.map((item) => ({
        ...item,
        monthLabel: formatMonthYear(item.month),
      })),
    [data]
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cotizaciones por Mes</CardTitle>
        <CardDescription>Últimos 6 meses</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full aspect-auto min-w-0">
          <AreaChart
            accessibilityLayer
            data={formattedData}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="monthLabel"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="line" />}
            />
            <Area
              dataKey="count"
              type="natural"
              fill="var(--chart-2)"
              fillOpacity={0.4}
              stroke="var(--chart-2)"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function TopSuppliersChart({ data }: { data: DashboardChartsData["topSuppliers"] }) {
  const chartConfig = {
    count: {
      label: "Cotizaciones",
      color: "var(--chart-3)",
    },
  } satisfies ChartConfig

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top Proveedores</CardTitle>
        <CardDescription>Por cantidad de cotizaciones</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full aspect-auto min-w-0">
          <BarChart
            accessibilityLayer
            data={data}
            layout="vertical"
            margin={{
              left: 0,
            }}
          >
            <CartesianGrid horizontal={false} />
            <YAxis
              dataKey="name"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value: string) => truncateLabel(value)}
              width={120}
            />
            <XAxis type="number" />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="line" />}
            />
            <Bar dataKey="count" fill="var(--chart-3)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function SectorActivityChart({ data }: { data: DashboardChartsData["sectorActivity"] }) {
  const chartConfig = {
    count: {
      label: "Solicitudes",
      color: "var(--chart-4)",
    },
  } satisfies ChartConfig

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sectores/Obras Activos</CardTitle>
        <CardDescription>Solicitudes en el último mes</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full aspect-auto min-w-0">
          <BarChart
            accessibilityLayer
            data={data}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              tickFormatter={(value: string) => truncateLabel(value)}
              height={120}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="dashed" />}
            />
            <Bar dataKey="count" fill="var(--chart-4)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function TopProductsChart({ data }: { data: DashboardChartsData["topProducts"] }) {
  const chartConfig = {
    count: {
      label: "Veces solicitado",
      color: "var(--chart-5)",
    },
  } satisfies ChartConfig

  return (
    <Card>
      <CardHeader>
        <CardTitle>Productos Más Solicitados</CardTitle>
        <CardDescription>Top 10 productos más pedidos</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full aspect-auto min-w-0">
          <BarChart
            accessibilityLayer
            data={data}
            layout="vertical"
            margin={{
              left: 0,
              right: 12,
            }}
          >
            <CartesianGrid horizontal={false} />
            <YAxis
              dataKey="name"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value: string) => truncateLabel(value)}
              width={150}
            />
            <XAxis type="number" />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent indicator="line" />}
            />
            <Bar dataKey="count" fill="var(--chart-5)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

// Custom tooltip for active users to show breakdown
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
function ActiveUsersTooltip({ active, payload }: any) {
  if (!active || !payload?.[0]?.payload) {
    return null
  }

  const data: DashboardChartsData["activeUsers"][number] = payload[0].payload

  return (
    <div className="rounded-lg border bg-background p-3 shadow-xl">
      <div className="mb-2 font-medium">{data.name}</div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="font-semibold">Score Total:</span>
          <span className="font-mono font-bold tabular-nums">{data.score}</span>
        </div>
        <div className="my-2 border-t" />
        <div className="space-y-1 text-muted-foreground">
          <div className="flex items-center justify-between gap-4">
            <span>Comentarios (x1):</span>
            <span className="font-mono tabular-nums">{data.breakdown.comments}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Solicitudes creadas (x3):</span>
            <span className="font-mono tabular-nums">{data.breakdown.requests}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Selecciones (x2):</span>
            <span className="font-mono tabular-nums">{data.breakdown.selections}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Aprobaciones (x2):</span>
            <span className="font-mono tabular-nums">{data.breakdown.approvals}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
/* eslint-enable @typescript-eslint/no-explicit-any */
/* eslint-enable @typescript-eslint/no-unsafe-member-access */
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

function ActiveUsersChart({ data }: { data: DashboardChartsData["activeUsers"] }) {
  const chartConfig = {
    score: {
      label: "Score de Actividad",
      color: "var(--chart-1)",
    },
  } satisfies ChartConfig

  return (
    <Card>
      <CardHeader>
        <CardTitle>Usuarios Más Activos</CardTitle>
        <CardDescription>Score combinado último mes (admin/supervisor)</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[350px] w-full aspect-auto min-w-0">
          <BarChart
            accessibilityLayer
            data={data}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="name"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              tickFormatter={(value: string) => truncateLabel(value)}
              height={120}
            />
            <ChartTooltip content={<ActiveUsersTooltip />} />
            <Bar dataKey="score" fill="var(--chart-1)" radius={4} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
